import { useRef, useState } from 'react'
import { Textarea } from '../ui/shadcn/textarea'
import { type Accounts } from '@backend/lib/db/schema/platform'
import { Button } from './shadcn/button'
import { type PostContent } from '@backend/lib/platforms/instagram'
import { cn } from '@/lib/utils'

type Props = {
  postContent: PostContent
  accounts: Accounts | undefined
  onCaptionChange?: (caption: string) => void
  onStartAuth: () => void
}

export function InstagramPostPreview({
  accounts,
  postContent,
  onCaptionChange,
  onStartAuth,
}: Props) {
  // ref
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  // carousel state
  const [currentSlide, setCurrentSlide] = useState(0)
  const [prevPostContent, setPrevPostContent] = useState(postContent)
  if (prevPostContent !== postContent) {
    setPrevPostContent(postContent)
    setCurrentSlide(0)
  }

  return (
    <div
      className={cn(
        'text-foreground mx-auto flex h-full w-full flex-col gap-4 rounded-lg',
        postContent?.type.includes('Story') ? 'px-4' : 'p-4',
      )}>
      {/* Header */}
      {!postContent?.type.includes('Story') && (
        <div className="flex items-center justify-between">
          <ProfileHeader
            imageUrl={accounts?.at(0)?.imageUrl}
            displayName={accounts?.at(0)?.displayName || 'Not authenticated'}
            isStory={false}
          />

          {!accounts?.at(0)?.displayName && <Button onClick={onStartAuth}>Connect</Button>}
        </div>
      )}

      {/* Image */}
      <div className="-mx-4 flex items-center justify-center">
        {postContent?.type === 'image' ? (
          <img
            src={postContent?.url}
            crossOrigin="anonymous"
            alt="Post"
            className="h-full w-full object-cover"
          />
        ) : postContent?.type === 'reel' ? (
          <video
            src={postContent?.url}
            className="h-auto w-full object-cover"
            controls
            autoPlay
            muted
          />
        ) : postContent?.type === 'carousel' ? (
          <div className="relative overflow-hidden">
            {/* Slides container */}
            <div
              className="flex transition-transform duration-300 ease-in-out"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}>
              {postContent?.urls.map(({ type, url }, index) => (
                <div key={index} className="w-full shrink-0">
                  {type === 'image' ? (
                    <img
                      src={url}
                      crossOrigin="anonymous"
                      alt="Post"
                      className="h-auto w-full object-cover"
                    />
                  ) : (
                    <video
                      src={url}
                      className="h-auto w-full object-cover"
                      controls
                      autoPlay
                      muted
                    />
                  )}
                </div>
              ))}
            </div>

            {/* Navigation dots */}
            {postContent?.urls.length > 1 && (
              <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 transform gap-1">
                {postContent?.urls.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`h-2 w-2 rounded-full transition-all ${
                      index === currentSlide ? 'bg-white' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            )}

            {/* Navigation arrows (optional - for better UX) */}
            {postContent?.urls.length > 1 && (
              <>
                {currentSlide > 0 && (
                  <button
                    onClick={() => setCurrentSlide(currentSlide - 1)}
                    className="absolute left-2 top-1/2 flex h-8 w-8 -translate-y-1/2 transform items-center justify-center rounded-full bg-black/20 text-white transition-all hover:bg-black/40">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>
                )}
                {currentSlide < postContent?.urls.length - 1 && (
                  <button
                    onClick={() => setCurrentSlide(currentSlide + 1)}
                    className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 transform items-center justify-center rounded-full bg-black/20 text-white transition-all hover:bg-black/40">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                )}
              </>
            )}
          </div>
        ) : postContent?.type === 'imageStory' ? (
          <div className="aspect-9/16 relative flex max-h-[600px] items-center justify-center overflow-hidden rounded-md bg-black">
            <img
              src={postContent?.url}
              className="max-h-full max-w-full object-contain"
              alt="Story"
            />
            <ProgressBar />
            <div className="absolute left-4 right-4 top-6">
              <ProfileHeader
                imageUrl={accounts?.at(0)?.imageUrl}
                displayName={accounts?.at(0)?.displayName || 'Not authenticated'}
                isStory={true}
              />
            </div>
          </div>
        ) : postContent?.type === 'videoStory' ? (
          <div className="aspect-9/16 relative flex max-h-[600px] items-center justify-center overflow-hidden rounded-md bg-black">
            <video
              src={postContent?.url}
              className="max-h-full max-w-full object-contain"
              controls
              autoPlay
              muted
            />
            <ProgressBar />
            <div className="absolute left-4 right-4 top-6">
              <ProfileHeader
                imageUrl={accounts?.at(0)?.imageUrl}
                displayName={accounts?.at(0)?.displayName || 'Not authenticated'}
                isStory={true}
              />
            </div>
          </div>
        ) : null}
      </div>

      {!postContent?.type.includes('Story') && (
        <>
          <div className="">
            {/* Action buttons */}
            <div className="flex items-center justify-between">
              <div className="flex gap-3">
                {/* Like */}
                <button className="text-gray-800">
                  <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path>
                  </svg>
                </button>

                {/* Comment */}
                <button className="text-gray-800">
                  <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
                  </svg>
                </button>

                {/* Share */}
                <button className="text-gray-800">
                  <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path>
                  </svg>
                </button>
              </div>

              {/* Save */}
              <button className="text-gray-800">
                <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"></path>
                </svg>
              </button>
            </div>

            {/* Likes */}
            <p className="text-sm font-semibold">1,234 likes</p>
          </div>

          {/* Caption */}
          <div className="">
            <Textarea
              ref={textareaRef}
              rows={1}
              placeholder="What do you want to talk about?"
              className="resize-none whitespace-pre-wrap border-none p-0 text-xs"
              value={postContent?.caption}
              onChange={(e) => onCaptionChange?.(e.target.value)}
            />
          </div>
        </>
      )}
    </div>
  )
}

const ProgressBar = () => {
  return (
    <div className="absolute left-2 right-2 top-2 flex gap-1">
      <div className="h-1 w-1/2 overflow-hidden rounded-full bg-white/30">
        <div className="h-full rounded-full bg-white"></div>
      </div>
      <div className="h-1 w-1/2 overflow-hidden rounded-full bg-white/30"></div>
    </div>
  )
}

const ProfileHeader = ({
  imageUrl,
  displayName,
  isStory,
}: {
  imageUrl: string | undefined
  displayName: string
  isStory: boolean
}) => {
  return (
    <div className="flex items-center gap-2">
      {/* Profile picture */}
      <div className="bg-linear-to-tr h-10 w-10 rounded-full from-yellow-400 to-purple-600 p-[2px]">
        <div className="bg-background flex h-full w-full items-center justify-center overflow-hidden rounded-full">
          {imageUrl ? (
            <img src={imageUrl} alt="Profile" className="h-full w-full object-cover" />
          ) : (
            <div className="bg-muted h-full w-full"></div>
          )}
        </div>
      </div>

      {/* Username and location */}
      <div className={cn('text-left', isStory && 'flex items-end gap-2 text-white')}>
        <span className={cn('text-[14px] font-semibold')}>
          {displayName || 'Not authenticated'}
        </span>
        {isStory && <span className="text-[12px] text-gray-500">17m</span>}
      </div>
    </div>
  )
}
